import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastService } from '@/app/shared/ui/notifications/toast.service';
import { triggerBlobDownload } from '@/app/shared/utils/file-download.util';
import {
  RegistryDownloadFormat,
  RegistryDownloadRequest,
  RegistryDownloadResult,
} from '../models/beneficiary-registry.model';
import { BeneficiaryRegistryRepository } from '../services/beneficiary-registry.repository';
import {
  RegistryDownloadNotice,
  toRegistryDownloadErrorNotice,
  toRegistryDownloadJobNotice,
} from './registry-download-notices';

@Injectable()
export class BeneficiaryRegistryDownloadStore {
  private readonly repository = inject(BeneficiaryRegistryRepository);
  private readonly toasts = inject(ToastService);
  private subscription: Subscription | null = null;

  private readonly _activeFormat = signal<RegistryDownloadFormat | null>(null);

  readonly activeFormat = this._activeFormat.asReadonly();
  readonly isDownloading = computed(() => this._activeFormat() !== null);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancel());
  }

  download(request: RegistryDownloadRequest): void {
    if (this.isDownloading()) return;
    this._activeFormat.set(request.format);

    this.subscription = this.repository.downloadRegistry(request).subscribe({
      next: (result) => this.handleResult(result),
      error: (cause: unknown) => {
        this.finish();
        this.notify(toRegistryDownloadErrorNotice(cause));
      },
      complete: () => this.finish(),
    });
  }

  cancel(): void {
    this.subscription?.unsubscribe();
    this.finish();
  }

  private handleResult(result: RegistryDownloadResult): void {
    if (result.mode === 'sync') {
      triggerBlobDownload(result.file.blob, result.file.fileName);
      return;
    }
    this.notify(toRegistryDownloadJobNotice(result.job));
  }

  private notify(notice: RegistryDownloadNotice): void {
    this.toasts[notice.tone](notice.title, {
      description: notice.description,
      persistent: notice.persistent,
    });
  }

  private finish(): void {
    this.subscription = null;
    this._activeFormat.set(null);
  }
}
